import { Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";

import { NO_DISPLAY, YES_DISPLAY } from "@/intake-forms/constants/yes-no-display";

import {
  StatementSettingsFormSchema,
  StatementSettingsFormType,
} from "../zod-schemas/statement-settings";

/** The linkIds the form knows how to store, taken from the schema so the two cannot drift. */
const STATEMENT_SETTINGS_LINK_IDS = Object.keys(
  StatementSettingsFormSchema.shape,
) as (keyof StatementSettingsFormType)[];

export type StatementSettingsField = {
  linkId: keyof StatementSettingsFormType;
  label: string;
};

/** Every field starts empty until the practice saves the form. */
export function emptyStatementSettings(): StatementSettingsFormType {
  return Object.fromEntries(
    STATEMENT_SETTINGS_LINK_IDS.map((linkId) => [linkId, null]),
  ) as StatementSettingsFormType;
}

function isStatementSettingsLinkId(
  linkId: string | undefined,
): linkId is keyof StatementSettingsFormType {
  return Boolean(linkId) && STATEMENT_SETTINGS_LINK_IDS.includes(linkId as never);
}

/**
 * Field order and labels for the Statement Settings card, read off the Questionnaire itself so
 * the card follows whatever the seeded resource says. Items the form has no field for are
 * skipped.
 */
export function getStatementSettingsFields(
  questionnaire: Questionnaire | undefined,
): StatementSettingsField[] {
  const fields: StatementSettingsField[] = [];

  for (const item of questionnaire?.item ?? []) {
    if (!isStatementSettingsLinkId(item.linkId)) continue;
    fields.push({ linkId: item.linkId, label: item.text?.trim() || item.linkId });
  }

  return fields;
}

/** Reads the Yes/No answers off a QuestionnaireResponse. */
export function parseStatementSettingsResponse(
  questionnaireResponse: QuestionnaireResponse | undefined,
): StatementSettingsFormType {
  const values = emptyStatementSettings();
  if (!questionnaireResponse?.item) return values;

  for (const item of questionnaireResponse.item) {
    if (!isStatementSettingsLinkId(item.linkId)) continue;

    const answer = item.answer?.[0];
    const stored = answer?.valueCoding?.display ?? answer?.valueString;
    if (stored === YES_DISPLAY || stored === NO_DISPLAY) {
      values[item.linkId] = stored;
    }
  }

  return values;
}
